import { log, LogLevel } from "logging/datadogBrowserLogs";
import React, { ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SectionTitle, Text } from "shared/components/typography/TitleStyles";

export type ErrorComponentType<P extends {}> = (
  props: { error: Error | null; resetState: () => void } & P,
) => ReactNode;

type ErrorBoundaryProps<P extends {}> = {
  children: ReactNode;
} & (WithErrorComponent<P> | WithoutErrorComponent);

type WithErrorComponent<P extends {}> = {
  ErrorComponent: ErrorComponentType<P>;
  errorProps: P;
};

type WithoutErrorComponent = {
  ErrorComponent?: never;
  errorProps?: never;
};

type ErrorState = {
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

export const DefaultError = () => {
  const { t } = useTranslation();
  return (
    <div>
      <SectionTitle>{t("En feil har oppstått")}</SectionTitle>
      <Text>
        {t(
          "Feilen er rapportert til utviklerne. Oppdater siden for å gå tilbake",
        )}
      </Text>
      <Text>
        {t(
          "Dersom feilen er gjentakende, send gjerne en liten beskrivelse om hvordan du gikk frem da feilen oppsto",
        )}
      </Text>
    </div>
  );
};

export class ErrorBoundary<
  ErrorComponentExtraProps extends {},
> extends React.Component<
  ErrorBoundaryProps<ErrorComponentExtraProps>,
  ErrorState
> {
  constructor(props: ErrorBoundaryProps<ErrorComponentExtraProps>) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Catch errors in any components below and re-render with error message
    this.setState({ error, errorInfo });
    log(
      LogLevel.error,
      errorInfo.componentStack || "[No component stack]",
      error?.stack || "[No error message]",
    );
  }

  resetState = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    const {
      props: { children, ErrorComponent, errorProps },
      state,
      resetState,
    } = this;

    if (state.errorInfo) {
      // Render custom error if present
      return ErrorComponent ? (
        <ErrorComponent
          error={state.error}
          resetState={resetState}
          {...errorProps}
        />
      ) : (
        <DefaultError />
      );
    }

    return children;
  }
}

export function withErrorBoundary<P extends {}>(
  Component: React.ComponentType<P>,
) {
  const displayName =
    Component.displayName ||
    Component.name ||
    "ComponentWrappedWithErrorBoundary";

  const ComponentWithErrorBoundary = (props: P) => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );

  ComponentWithErrorBoundary.displayName = displayName;

  return ComponentWithErrorBoundary;
}
